import React, {
  useState, useCallback, useEffect, useMemo,
} from 'react';
import {
  Loader, Image, Modal, Input, Table, Button, Label, Icon, Dropdown, Popup,
} from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  Other,
} from 'simplydo/interfaces';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import api from 'api';
import reportsApi from 'utils/reports';
import util from 'utils/utils';
import styled from 'styled-components';
import ConfigurableTable from 'components/lib/ConfigurableTable';

const AssessmentsDashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  > .dashboard-title {
    display: block;
    margin: 0;
    font-weight: 600;
  }
  > table {
    min-width: 100%;
  }
  .download-report {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    margin: 10px 0;
  }
`;

const AssessmentsInfo = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;
  border-radius: 5px;
  /* border: 1px solid #e9ebee; */
  cursor: pointer;
  &:hover {
    background-color: #f5f6f8;
  }
  > span {
    margin: 0;
    display: block;
    font-size: 0.9em;
  }
`;

const CompletedAssessmentsInfo = styled.div<{ submittedAssessmentProgress?: number }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  > span {
    display: block;
    margin: 0;
    color: black;
    ${({ submittedAssessmentProgress }) => (submittedAssessmentProgress === 100) && 'color: green;'}
    ${({ submittedAssessmentProgress }) => (submittedAssessmentProgress === 0) && 'color: red;'}
  }
  > .number {
    font-size: 1.3em;
    font-weight: 600;
  }
`;

const UserAssessmentsTableContainer = styled.div`
  overflow-x: auto;
  width: 100%;
  > table {
    min-width: 100%;
  }
  .idea-name {
    display: flex;
    flex-direction: row;
    align-items: center;
    > img {
      width: 25px;
      height: 25px;
      object-fit: cover;
      border-radius: 5px;
      margin-right: 5px;
    }
    > a {
      display: block;
      color: black;
      text-decoration-line: underline;
    }
  }
`;

type IDashboardAssessmentIdea = Other.IIdea;

type IDashboardAssessment = {
  assessor: string,
  ownerAssessor: Other.IUser,
  ideas: IDashboardAssessmentIdea[],
  totalSubmittedAssessments: number,
  hasSubmissionsWithConflict: boolean,
  totalStartedAssessments: number,
  totalAssessments: number,
  submittedAssessmentProgress: number,
  startedAssessmentProgress: number,
}

const AssessorIdeas = ({
  user, data, isOpen, onClose, challenge, unsubmitAssessment, unsubmittingAssessment,
}: {
  user: Other.IUserMe, data: IDashboardAssessment, isOpen: boolean, onClose: () => void, challenge: Other.IChallenge, unsubmitAssessment: (ideaId, assessorId) => void, unsubmittingAssessment: string,
}) => {
  const ideaTemplate = (challenge.ideaTemplateData);
  if (!data || !ideaTemplate) return null;
  const { ideas } = data;
  const hasConflictOfInterest = ideas.some((idea) => idea.assessments?.length && idea.assessments[0].conflictOfInterest?.hasConflictOfInterest);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      size='fullscreen'
    >
      <UserAssessmentsTableContainer>
        <Table celled style={{ overflowX: 'scroll' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Idea</Table.HeaderCell>
              {hasConflictOfInterest ? <Table.HeaderCell>Conflict of Interest</Table.HeaderCell> : null}
              {ideaTemplate.body.map(({ name }) => (
                <Table.HeaderCell>Section: {name}</Table.HeaderCell>
              ))}
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {ideas.map((idea) => {
              const {
                _id, name, assessments = [],
              } = idea;
              const userAssessment = assessments[0];
              const absoluteScore = userAssessment?.assessment ? (Object.values(userAssessment.assessment).map((value) => (value.score ?? 0)).reduce((a, b) => (a + b), 0)) : 0;
              const absoluteTotalScore = ideaTemplate.body.map((section) => section.assessment?.availableScore ?? 0).reduce((a, b) => (a + b), 0);

              return (
                <Table.Row>
                  <Table.Cell singleLine style={{ maxWidth: 250, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                    <div className='idea-name'>
                      <img src={util.ideaCoverImage(idea)} alt='Idea cover' />
                      <Link to={`/ideas/${_id}`} style={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>{name}</Link>
                    </div>
                  </Table.Cell>
                  {hasConflictOfInterest
                    ? (
                      <Table.Cell singleLine>
                        {userAssessment?.conflictOfInterest?.hasConflictOfInterest
                          ? (
                            <div>
                              <div style={{ marginBottom: 5 }}>
                                <Icon color='red' name='warning circle' />
                                <span><b>Conflict of interest declared</b></span>
                              </div>
                              {userAssessment.conflictOfInterest.notes
                                ? (
                                  <div>
                                    <span>{userAssessment.conflictOfInterest.notes}</span>
                                  </div>
                                ) : null
                              }
                            </div>
                          ) : (
                            'N/A'
                          )
                        }
                      </Table.Cell>
                    ) : null}
                  {ideaTemplate.body.map(({ id, assessment }) => {
                    const defaultCell = <Table.Cell><i>Not yet completed</i></Table.Cell>;
                    const conflictDefaultCell = <Table.Cell><i>N/A</i></Table.Cell>;
                    if (userAssessment?.conflictOfInterest?.hasConflictOfInterest) return conflictDefaultCell;
                    if (!userAssessment) return defaultCell;
                    const userSectionAssessment = userAssessment.assessment[id];
                    if (!userSectionAssessment) return defaultCell;
                    return (
                      <Table.Cell style={{ minWidth: 200 }}>
                        <span><b>Score: {userSectionAssessment.score || 0}/{assessment.availableScore}</b></span>
                        <br /><br />
                        <span>{userSectionAssessment.notes || 'No notes'}</span>
                      </Table.Cell>
                    );
                  })}
                  {userAssessment
                    ? (
                      <Table.Cell collapsing>
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                          {!userAssessment?.conflictOfInterest?.hasConflictOfInterest
                            && <Label
                              size='small'
                              color='violet'
                            >
                              {absoluteScore} / {absoluteTotalScore} - {Math.round(userAssessment.score || 0)}%
                            </Label>
                          }
                          {userAssessment.startedAt || userAssessment.isSubmitted
                            ? (
                              <>
                                {userAssessment.isSubmitted
                                  ? (
                                    <Label size='small' color='green'>Submitted</Label>
                                  ) : (
                                    <Label size='small' color='orange'>Not yet submitted</Label>
                                  )
                                }
                              </>
                            )
                            : (<Label size='small' color='red'>Not yet started</Label>)
                          }
                          {userAssessment.isSubmitted && util.canManageChallenge(user, challenge)
                            ? (
                              <Button
                                size='tiny'
                                content='Unsubmit'
                                onClick={() => unsubmitAssessment(_id, data.assessor)}
                                loading={unsubmittingAssessment === `${_id}-${data.assessor}`}
                                style={{ height: 24, padding: '0 20px' }}
                              />
                            ) : null
                          }
                        </div>
                      </Table.Cell>
                    ) : null
                  }
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      </UserAssessmentsTableContainer>
    </Modal>
  );
};

const AssessorTable = ({
  assessors, setOpenAssessor, onSort, sort, unassignAssessorFromAllIdeas,
}: {
  assessors: IDashboardAssessment[],
  challenge: Other.IChallenge,
  setOpenAssessor: (assessorId: string) => void, onSort: (key: string) => void, sort: { key: string, direction: 'asc' | 'desc' },
  unassignAssessorFromAllIdeas: (assessorId: string) => void,
}) => {
  const { t } = useTranslation();
  return (
    <ConfigurableTable
      tableKey='assessmentsDashboard'
      data={assessors}
      keyExtractor={(item) => item.assessor}
      restrictOverflow
      onSort={onSort}
      sort={{ key: sort.key as keyof IDashboardAssessment, direction: sort.direction }}
      columns={[
        {
          key: 'fullName',
          name: 'Assessor',
          sortable: true,
          render: ({ item }) => <>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Link to={`/users/${item.assessor}`}><Image style={{ objectFit: 'cover' }} avatar src={util.avatarUrl(item?.ownerAssessor)} /></Link>
              <Link style={{ marginLeft: 5 }} to={`/users/${item.assessor}`}>{item.ownerAssessor.profile.fullName}</Link>
            </div>
          </>,
        },
        {
          key: 'totalAssessments',
          name: 'Total Assessments',
          sortable: true,
          render: ({ item }) => (
            <>
              <AssessmentsInfo onClick={() => setOpenAssessor(item.assessor)}>
                <span>{item.totalAssessments}</span>
              </AssessmentsInfo>
            </>
          ),
        },
        {
          key: 'notStartedAssessments',
          name: 'Not Started',
          render: ({ item }) => (
            <>
              <AssessmentsInfo onClick={() => setOpenAssessor(item.assessor)}>
                <span>{item.totalAssessments - item.totalStartedAssessments - item.totalSubmittedAssessments}</span>
              </AssessmentsInfo>
            </>
          ),
        },
        {
          key: 'inProgressAssessment',
          name: 'In Progress',
          render: ({ item }) => (
            <>
              <AssessmentsInfo onClick={() => setOpenAssessor(item.assessor)}>
                <span>{item.totalStartedAssessments}</span>
              </AssessmentsInfo>
            </>
          ),
        },
        {
          key: 'submittedAssessments',
          name: 'Submitted',
          render: ({ item }) => (
            <>
              <AssessmentsInfo onClick={() => setOpenAssessor(item.assessor)}>
                <span>{item.totalSubmittedAssessments} submitted {item.hasSubmissionsWithConflict ? (
                  <Popup
                    trigger={<Icon size="large" color="red" name="warning sign" />}
                    content={"This assessor has reported a conflict of interest on one or more ideas."}
                  /> ) : null}</span>
              </AssessmentsInfo>
            </>
          ),
        },
        {
          key: 'assessmentsCompleted',
          name: 'Progress',
          sortable: true,
          render: ({ item }) => (
            <CompletedAssessmentsInfo submittedAssessmentProgress={item.submittedAssessmentProgress}>
              <span className='number'>
                {item.submittedAssessmentProgress}%
              </span>
              <span>
                Submitted
              </span>
            </CompletedAssessmentsInfo>
          ),
        },
        {
          key: 'openAssessments',
          name: '',
          center: true,
          collapsing: true,
          render: ({ item }) => (
            <>
              <Button
                icon='eye'
                content={t('assessments.dashboard.view-assessments')}
                onClick={() => setOpenAssessor(item.assessor)}
                size='small'
              />
              <Dropdown
                direction='left'
                trigger={(
                  <Button
                    icon='settings'
                    size='small'
                  />
                )}
                icon={null}
              >
                <Dropdown.Menu>
                  <Dropdown.Item onClick={() => unassignAssessorFromAllIdeas(item.assessor)}>
                    <Icon name='remove user' />
                    Unassign this assessor from all ideas
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </>
          ),
        },
      ]}
    />
  );
}

type IAssessmentsDashboard = {
  challenge: Other.IChallenge,
  getAssignedAssessments: () => void,
  myAssignedAssessments: Other.IIdeaAssignment[],
  setMyAssignedAssessments: (assessments: Other.IIdeaAssignment[]) => void,
  demoingTour?: string,
}

const AssessmentsDashboard = ({
  challenge,
}: IAssessmentsDashboard) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [assessors, setAssessors] = useState<IDashboardAssessment[]>([]);
  const [totals, setTotals] = useState<{ assessors: number, ideas: number }>({ assessors: 0, ideas: 0 });
  const [sort, setSort] = useState<'assessmentsCompleted' | 'fullName' | 'totalAssessments' | ''>('assessmentsCompleted');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [openAssessor, setOpenAssessor] = useState<string>(null);
  const [searchValue, setSearchValue] = useState<string>('');
  // Format: {idea_id}-{user_id}
  const [unsubmittingAssessment, setUnsubmittingAssessment] = useState<string>('');
  const [downloadingAssessmentReport, setDownloadingAssessmentReport] = useState<boolean>(false);
  const [downloadedAssessmentReport, setDownloadedAssessmentReport] = useState<string>('');

  const challengeId = challenge._id;
  const user = useSelector((state: { user: Other.IUserMe | null }) => state?.user);

  const openAssessorData = useMemo(() => assessors.find((a) => a.assessor === openAssessor), [assessors, openAssessor]);

  const getAssessmentDashboard = useCallback((shouldSetLoading) => {
    setLoading(shouldSetLoading);
    api.challenges.getAssessmentDashboard(challengeId, searchValue, ({ assessors: newAssessors, totals: newTotals }) => {
      setAssessors(newAssessors);
      setTotals(newTotals);
      setLoading(false);
    }, (err) => {
      setLoading(false);
      toast.error(err.message);
    });
  }, [challengeId, searchValue]);

  const unsubmitAssessment = useCallback((ideaId, assessorId) => {
    setUnsubmittingAssessment(`${ideaId}-${assessorId}`);
    api.ideas.unsubmitAssessment(ideaId, assessorId, () => {
      toast.success('Unsubmitted assessment');
      setUnsubmittingAssessment('');
      getAssessmentDashboard(false);
    }, () => {
      toast.error('Unsubmitted failed');
      setUnsubmittingAssessment('');
    });
  }, [getAssessmentDashboard]);

  const generateAssessmentReport = useCallback((withNotes: boolean) => {
    toast.success('Your report will download shortly.');
    reportsApi.token = api.token;
    setDownloadingAssessmentReport(true);
    api.reports.challengeAssessmentReport(challengeId, { includeNotes: withNotes }, (c) => {
      setDownloadedAssessmentReport(c.link);
      window.location = c.link;
      setDownloadingAssessmentReport(false);
    }, () => {
      toast.error('Error unable to generate report');
      setDownloadingAssessmentReport(false);
    });
  }, [challengeId]);

  useEffect(() => {
    getAssessmentDashboard(true);
  }, [getAssessmentDashboard]);

  const sortedAssessors = useMemo(() => {
    if (!sort) return assessors;
    return [...assessors].sort((a, b) => {
      if (sort === 'assessmentsCompleted') {
        if (sortDirection === 'asc') return a.submittedAssessmentProgress - b.submittedAssessmentProgress;
        return b.submittedAssessmentProgress - a.submittedAssessmentProgress;
      }
      if (sort === 'fullName') {
        if (sortDirection === 'asc') return ((a.ownerAssessor?.profile.fullName ?? '') > (b.ownerAssessor?.profile.fullName ?? '') ? -1 : 1);
        return ((a.ownerAssessor?.profile.fullName ?? '') < (b.ownerAssessor?.profile.fullName ?? '') ? -1 : 1);
      }
      if (sort === 'totalAssessments') {
        if (sortDirection === 'asc') return a.totalAssessments - b.totalAssessments;
        return b.totalAssessments - a.totalAssessments;
      }
      return a.submittedAssessmentProgress - b.submittedAssessmentProgress;
    });
  }, [assessors, sort, sortDirection]);

  const unassignAssessorFromAllIdeas = useCallback((assessorId) => {
    const usingAssessor = assessors.find((a) => a.assessor === assessorId);
    util.confirm(
      'Unassign assessor from all ideas',
      'Are you sure you want to unassign this assessor from all ideas? All their existing assessments will be deleted. This action cannot be undone.',
    ).then(() => {
      api.challenges.unassignAssessors(
        challengeId,
        usingAssessor.ideas.map((idea) => idea._id),
        [assessorId],
        () => {
          setAssessors((prevAssessors) => prevAssessors.filter((a) => a.assessor !== assessorId));
        },
        (err) => {
          toast.error(err.message);
        }
      )
    }).catch(() => {});
  }, [assessors, challengeId]);

  const onSort = useCallback((sortKey) => {
    if (sortKey !== sort) {
      setSort(sortKey);
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSort('');
    }
  }, [sort, sortDirection]);

  return (
    <AssessmentsDashboardContainer>
      <Input
        style={{ maxWidth: '300px', marginBottom: 10 }}
        onChange={(e, { value }) => setSearchValue(value)}
        placeholder={`Search ${util.pluralise(totals.ideas, 'idea', 'ideas')}/${util.pluralise(totals.assessors, 'assessor', 'assessors')}...`}
        value={searchValue}
        icon='search'
      />
      <AssessorIdeas
        user={user}
        data={openAssessorData}
        challenge={challenge}
        isOpen={!!openAssessor}
        onClose={() => setOpenAssessor('')}
        unsubmitAssessment={unsubmitAssessment}
        unsubmittingAssessment={unsubmittingAssessment}
      />
      {loading
        ? (
          <Loader active inline='centered' />
        ) : (
          <>
            <AssessorTable
              assessors={sortedAssessors}
              challenge={challenge}
              setOpenAssessor={setOpenAssessor}
              sort={{ key: sort, direction: sortDirection }}
              onSort={onSort}
              unassignAssessorFromAllIdeas={unassignAssessorFromAllIdeas}
            />
          </>
        )
      }
      {util.canManageChallenge(user, challenge)
        ? (
          <div className='download-report'>
            {downloadedAssessmentReport
              ? (
                <Button
                  secondary icon='cloud download' content='Download assessment report' as='a' href={downloadedAssessmentReport} style={{ margin: 0 }}
                />
              ) : (
                <Dropdown
                  trigger={(
                    <Button
                      secondary icon='file excel' content='Generate assessment report' loading={downloadingAssessmentReport} style={{ margin: 0 }}
                    />
                  )}
                  icon={null}
                >
                  <Dropdown.Menu>
                    <Dropdown.Header>Generate assessment report</Dropdown.Header>
                    <Dropdown.Item onClick={() => generateAssessmentReport(true)}>... <b>with</b> assessor notes</Dropdown.Item>
                    <Dropdown.Item onClick={() => generateAssessmentReport(false)}>... <b>without</b> assessor notes</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              )
            }

          </div>
        ) : null
      }
    </AssessmentsDashboardContainer>
  );
};

export default AssessmentsDashboard;
